import { Print } from "@mui/icons-material";
import { Box, type BoxProps, Button } from "@mui/material";
import {
  DataGridPro,
  type DataGridProProps,
  type GridToolbarProps,
  GridToolbarQuickFilter,
  type GridValidRowModel,
} from "@mui/x-data-grid-pro";
import { memo, useRef } from "react";
import { type Except } from "type-fest";

import { type ColumnData, type RowData } from "~types/datagrid";

import { PrintWidget } from "../../atoms/DataGrid/Widgets/PrintWidget";
import {
  EmptyState,
  type EmptyStateProps,
  ErrorState,
  type ErrorStateProps,
  ListLoadingState,
  type ListLoadingStateProps,
} from "../../atoms/SectionStates";

const MIN_HEIGHT: React.CSSProperties["height"] = "372px";

interface ToolbarCustomProps {
  widgetOptions?: { show?: boolean; showPrintWidget?: boolean };
}

function getToolbar({ widgetOptions = {} }: ToolbarCustomProps) {
  const { show = true, showPrintWidget = true } = widgetOptions;

  return function Toolbar({ showQuickFilter, quickFilterProps, ...props }: GridToolbarProps) {
    return (
      <Box
        sx={{
          "& .MuiFormControl-root": {
            width: "100%",
          },
          alignItems: { sm: "center", xs: "stretch" },
          display: "flex",
          flexDirection: { sm: "row", xs: "column" },
          gap: 2,
          justifyContent: "space-between",
          px: 1, // Reduced horizontal padding
          py: 2,
          width: "100%",
        }}
      >
        {Boolean(showQuickFilter) && (
          <Box sx={{ width: { lg: "50%", xs: "100%" } }}>
            <GridToolbarQuickFilter {...quickFilterProps} />
          </Box>
        )}
        {show && (
          <>
            {!props.children && <i />}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "flex-end",
                marginRight: 0,
              }}
            >
              {props.children}
              {showPrintWidget && (
                <Button
                  startIcon={<Print />}
                  color="primary"
                  variant="text"
                  onClick={() => {
                    // Find the PrintWidget in the DOM and click it
                    const printButton = document.querySelector('[aria-label="Print"]');
                    if (printButton instanceof HTMLElement) {
                      printButton.click();
                    }
                  }}
                >
                  Print
                </Button>
              )}
              {/* Hidden PrintWidget to handle the actual printing functionality */}
              {showPrintWidget && (
                <Box sx={{ display: "none" }}>
                  <PrintWidget slotProps={{ button: { size: "small" } }} />
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    );
  };
}

const sharedSlotProps: ListLoadingStateProps["slotProps"] = {
  container: {
    height: `calc(${MIN_HEIGHT} - 2px)`,
    sx: {
      border: "none",
      borderRadius: "0.25rem",
    },
  },
};

export type DataGridBaseProps<T extends GridValidRowModel> = Except<
  DataGridProProps<RowData<T>>,
  "columns" | "rows"
> & {
  /** Column data. This should be memoized. */
  columns: readonly ColumnData<T>[];
  /** Row data. This should be memoized. */
  rows: readonly RowData<T>[];
  headerWidgets?: React.ReactNode;
  status?: Utils.QueryStatus;
  toolbarCustomProps?: ToolbarCustomProps;
  rowHeight?: number | "auto" | string;
};
export type DataGridSlotProps<T extends GridValidRowModel> = Pick<
  DataGridProProps<RowData<T>>,
  "slotProps" | "slots"
> & {
  slotProps?: {
    container?: BoxProps;
    empty?: EmptyStateProps;
    error?: ErrorStateProps;
    loading?: ListLoadingStateProps;
  };
  slots?: {
    empty?: React.ReactNode;
    error?: React.ReactNode;
    loading?: React.ReactNode;
  };
};
export type DataGridProps<T extends GridValidRowModel> = Except<DataGridBaseProps<T>, "slotProps" | "slots"> &
  DataGridSlotProps<T>;

function NoRowsOverlayWrapper(props?: EmptyStateProps) {
  return function NoRowsOverlay() {
    return <EmptyState slotProps={sharedSlotProps} {...props} />;
  };
}

export function DataGrid<T extends GridValidRowModel>({
  headerWidgets,
  status = "success",
  toolbarCustomProps = {},
  rowHeight = "auto" as unknown as number,
  ...props
}: DataGridProps<T>) {
  const ref = useRef<HTMLDivElement>(null);

  const noRowsOverlay = memo(
    NoRowsOverlayWrapper({
      ...props.slotProps?.empty,
      slotProps: {
        ...props.slotProps?.empty?.slotProps,
        container: {
          ...props.slotProps?.empty?.slotProps?.container,
          height: "100%",
        },
      },
    }),
  );

  return (
    <Box
      {...props.slotProps?.container}
      sx={{
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        overflowX: "hidden",
        width: "100%",
        ...props.slotProps?.container?.sx,
      }}
    >
      {status === "error" &&
        (props.slots?.error ?? <ErrorState slotProps={sharedSlotProps} {...props.slotProps?.error} />)}
      {status === "pending" &&
        (props.slots?.loading ?? (
          <ListLoadingState count={7} slotProps={sharedSlotProps} {...props.slotProps?.loading} />
        ))}
      {status === "success" && (
        /**
         * We receive the following typescript error;
         *
         * @example
         *  Types of property 'type' are incompatible.
              Type 'GridColType | undefined' is not assignable to type '"singleSelect"'.
                Type 'undefined' is not assignable to type '"singleSelect"'.ts(2322)
         */
        // @ts-expect-error `type` is allowed to be `undefined` according to `node_modules/@mui/x-data-grid/models/colDef/gridColDef.d.ts`
        <DataGridPro<T>
          disableColumnFilter
          filterMode="client"
          getRowHeight={() => (typeof rowHeight === "number" ? rowHeight : "auto")}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          pagination
          {...props}
          getRowId={(row) => row.rowId}
          initialState={{
            ...props.initialState,
            pagination: {
              ...props.initialState?.pagination,
              paginationModel: { pageSize: 5, ...props.initialState?.pagination?.paginationModel },
            },
          }}
          ref={ref}
          slotProps={{
            ...props.slotProps,
            footer: {
              ...props.slotProps?.footer,
              sx: { display: "flex", justifyContent: "center", ...props.slotProps?.footer?.sx },
            },
            toolbar: {
              children: headerWidgets,
              showQuickFilter: true,
              ...props.slotProps?.toolbar,
              quickFilterProps: {
                debounceMs: 333,
                fullWidth: true,
                size: "medium",
                sx: {
                  "& .MuiInputBase-root": {
                    pl: 2,
                    width: "100%",
                  },
                },
                ...props.slotProps?.toolbar?.quickFilterProps,
              },
              style: {
                paddingBottom: 0,
                paddingRight: 0, // Remove right padding
                ...props.slotProps?.toolbar?.style,
              },
            },
          }}
          slots={{ noRowsOverlay, toolbar: getToolbar(toolbarCustomProps), ...props.slots }}
          componentsProps={{
            basePopper: {
              // Fix for ARIA role issues in the DataGrid
              slotProps: {
                root: {
                  role: "presentation",
                },
              },
            },
          }}
          sx={{
            " .MuiDataGrid-cell": { alignItems: "center", padding: "0.5rem" },
            " .MuiDataGrid-columnHeader": {
              " .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" },
              "&:focus-within": { outline: "unset" },
            },
            " .MuiDataGrid-toolbarContainer": {
              boxSizing: "border-box", // Include padding in width calculation
              maxWidth: "100%", // Ensure toolbar doesn't exceed grid width
              overflowX: "hidden", // Prevent horizontal overflow
              padding: "0", // Remove default padding
            },
            ...(props.rows?.length
              ? {}
              : {
                  " .MuiDataGrid-overlayWrapper": {
                    display: "inline-grid !important",
                  },
                  " .MuiDataGrid-virtualScroller": { overflow: "hidden !important" },
                }),
            border: "none",
            overflow: "hidden", // Prevent content from extending beyond container
            ...props.sx,
          }}
        />
      )}
    </Box>
  );
}
